/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import sortby from 'lodash.sortby';
import {translate} from '@ion/core/src/locale/locale';
import {
  GenericObject,
  IonTagReportType,
  MESSAGE_TYPES,
  SC_TAG_TYPES
} from '@ion/core/src/types/entities';
import RootManager from '@ion/dls/src/client/RootManager';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useDeviceSensors} from '@ion/query/src/hooks/useDeviceSensors';
import {systemOfUnitsSelector} from '@/store/selectors/login-selectors';
import IonTagSensorGraphs from './sc/reports/IonTagSensorGraphs';
import IonTagDetailsAccordion from './IonTagDetailsAccordion';
import IonTagDetailsHeader from './IonTagDetailsHeader';

export type IonTagDetailsPanelProps = {
  device: IonTagReportType;
  selectedIonTag?: GenericObject;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    ionTagDetailPanelWrapper : {
      position                       : 'absolute',
      overflowY                      : 'auto',
      overflowX                      : 'hidden',
      top                            : (topValue) => `${topValue}px`,
      bottom                         : '5px',
      left                           : '0',
      right                          : '0',
      [theme.breakpoints.down('sm')] : {
        position : 'relative',
        top      : '0 !important'
      }
    }
  };
});

const getPositionTop = (): number => {
  const element   = RootManager.getContainer('ionTagDetailTabContainer');
  const preceding = element?.previousElementSibling;
  const height    = preceding?.getBoundingClientRect().height;
  return height ?? height < 100 ? height + 50 : 120;
};

const IonTagDetailsPanel = ({device, selectedIonTag}: IonTagDetailsPanelProps) => {
  const classes       = useStyles(getPositionTop());
  const threshold     = {max: undefined, min: undefined};
  const noDataLabel   = translate('tracking:ionTagDetailsPanel.noDataAvailable');
  const systemOfUnits = useSelector(systemOfUnitsSelector);
  const {name, id}    = selectedIonTag || {};
  const {isLoading, reports} = useDeviceSensors({
    msgType  : MESSAGE_TYPES.TAG_REPORT,
    deviceId : id?.toString(),
    systemOfUnits
  });

  const {
    accumulatedResults = {},
    currentReport = {}
  } = reports;

  const supportedIonTagSensors = SC_TAG_TYPES[
    currentReport?.tagReport?.deviceDataConverted?.pegBehavior?.source?.title
  ];

  const sensorDataForDevice = useMemo(() => (
    supportedIonTagSensors?.map?.((sensorType: string) => {
      return {
        data       : sortby(accumulatedResults[sensorType]?.data, 'eventTime'),
        title      : sensorType,
        sensorType : sensorType,
        threshold,
        noDataLabel
      };
    })
  ), [accumulatedResults, supportedIonTagSensors]);

  return (
    <>
      <IonTagDetailsHeader ionTagName={name}/>
      <div className={classes.ionTagDetailPanelWrapper} id='ionTagDetailTabContainer'>
        <IonTagDetailsAccordion device={device} />
        <IonTagSensorGraphs data={sensorDataForDevice} loading={isLoading} />
      </div>
    </>
  );
};

export default memo(IonTagDetailsPanel);
